import { ICache } from './cache';
import { LRUCache as Lru } from 'lru-cache'; // Using the imported LRU

// Default options for the LRU cache
const DEFAULT_MAX_SIZE = 100;
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

export class LRUCache<T = any> implements ICache<T> {
  private cache: Lru<string, T>;

  constructor(maxSize: number = DEFAULT_MAX_SIZE, defaultTTL: number | null = DEFAULT_TTL_MS) {
    const options: Lru.Options<string, T, unknown> = {
      max: maxSize,
    };
    if (defaultTTL !== null) {
      options.ttl = defaultTTL;
    }

    this.cache = new Lru<string, T>(options);
    console.log(`LRUCache (using 'lru-cache' library) initialized with maxSize: ${maxSize}, defaultTTL: ${defaultTTL === null ? 'none' : defaultTTL + 'ms'}`);
  }

  async get(key: string): Promise<T | null | undefined> {
    // The 'lru-cache' library's get method automatically handles TTL.
    // It returns undefined if the item is not found or has expired.
    const value = this.cache.get(key);
    return value === undefined ? null : value; // Match ICache's expected null for not found/expired
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    // 'lru-cache' allows setting a specific TTL per item.
    // If 'ttl' is not provided, it will use the default TTL from the constructor.
    // If defaultTTL was null, and no ttl is provided here, it won't expire by time.
    if (ttl !== undefined) {
      this.cache.set(key, value, { ttl });
    } else {
      this.cache.set(key, value);
    }
  }

  async has(key: string): Promise<boolean> {
    // The 'lru-cache' library's has method also respects TTL.
    return this.cache.has(key);
  }

  async delete(key: string): Promise<void> {
    this.cache.delete(key);
  }

  async isAvailable(): Promise<boolean> {
    // Local in-memory cache is always available as long as the instance exists.
    return true;
  }

  /**
   * Clears the entire cache.
   */
  async clear(): Promise<void> {
    this.cache.clear();
    console.log("LRUCache (using 'lru-cache' library): Cache cleared.");
  }

  /**
   * Gets the current size of the cache.
   */
  getCurrentSize(): number {
    return this.cache.size;
  }
}
